// Mantenimiento M1: importación del inventario desde Excel o CSV (librería xlsx del proyecto) y exportación de lo
// visible. Pasos: plantilla descargable → leer el archivo → relación de columnas (automática, editable) → vista previa
// con validación → aviso de duplicados por código (bloquea esa fila) o por número de serie (aviso) → confirmación.
// La aptitud de seguridad no se importa: cada activo nace con la aptitud por defecto y se cambia de a uno.

import * as XLSX from 'xlsx';
import {
  CATEGORIAS, CRITICIDADES, ESTADOS, ETIQUETA_APTITUD, ETIQUETA_CRITICIDAD, ETIQUETA_ESTADO, LISTA_CATEGORIAS, etiquetaCategoria,
  type Categoria, type Criticidad, type EstadoOperativo, type FilaActivo,
} from './mant-tipos';
import { normalizarCodigo } from './mant-logica';

export type CampoImport = 'name' | 'code' | 'category' | 'manufacturer' | 'brand' | 'model' | 'serial_number' | 'manufacture_year'
  | 'site' | 'area' | 'location_detail' | 'criticality' | 'operational_status' | 'description' | 'external_provider'
  | 'acquired_on' | 'installed_on' | 'useful_life_years';

export interface ColumnaImport { campo: CampoImport; encabezado: string; requerido?: boolean; alias: string[]; ejemplo: string }

export const COLUMNAS_IMPORT: ColumnaImport[] = [
  { campo: 'name', encabezado: 'Nombre', requerido: true, alias: ['nombre', 'activo', 'equipo', 'descripcion corta'], ejemplo: 'Torno paralelo' },
  { campo: 'category', encabezado: 'Categoría', requerido: true, alias: ['categoria', 'tipo', 'clase'], ejemplo: 'Máquina o equipo de producción' },
  { campo: 'code', encabezado: 'Código', alias: ['codigo', 'cod', 'id interno', 'tag'], ejemplo: '' },
  { campo: 'manufacturer', encabezado: 'Fabricante', alias: ['fabricante'], ejemplo: 'Industrias Andinas' },
  { campo: 'brand', encabezado: 'Marca', alias: ['marca'], ejemplo: 'Andes' },
  { campo: 'model', encabezado: 'Modelo', alias: ['modelo'], ejemplo: 'TP-200' },
  { campo: 'serial_number', encabezado: 'Número de serie', alias: ['numero de serie', 'serie', 'n serie', 'no serie', 'serial'], ejemplo: 'SN-000123' },
  { campo: 'manufacture_year', encabezado: 'Año de fabricación', alias: ['ano de fabricacion', 'ano', 'year'], ejemplo: '2019' },
  { campo: 'site', encabezado: 'Sede', alias: ['sede', 'sucursal', 'centro de trabajo'], ejemplo: 'Matriz' },
  { campo: 'area', encabezado: 'Área', alias: ['area', 'seccion', 'departamento'], ejemplo: 'Taller mecánico' },
  { campo: 'location_detail', encabezado: 'Ubicación específica', alias: ['ubicacion especifica', 'ubicacion', 'lugar'], ejemplo: 'Junto a la puerta 2' },
  { campo: 'criticality', encabezado: 'Criticidad', alias: ['criticidad', 'prioridad'], ejemplo: 'Alta' },
  { campo: 'operational_status', encabezado: 'Estado operativo', alias: ['estado operativo', 'estado'], ejemplo: 'Operativo' },
  { campo: 'description', encabezado: 'Descripción', alias: ['descripcion', 'observaciones', 'notas'], ejemplo: '' },
  { campo: 'external_provider', encabezado: 'Proveedor externo', alias: ['proveedor externo', 'proveedor', 'contratista'], ejemplo: '' },
  { campo: 'acquired_on', encabezado: 'Fecha de adquisición', alias: ['fecha de adquisicion', 'adquisicion', 'fecha de compra'], ejemplo: '2020-03-15' },
  { campo: 'installed_on', encabezado: 'Fecha de instalación', alias: ['fecha de instalacion', 'instalacion'], ejemplo: '' },
  { campo: 'useful_life_years', encabezado: 'Vida útil (años)', alias: ['vida util', 'vida util anos', 'vida util (anos)'], ejemplo: '10' },
];

export const sinTildes = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9()]+/g, ' ').trim();

/** Relación automática encabezado del archivo → campo (por nombre o alias, sin tildes ni mayúsculas). */
export function mapeoAutomatico(encabezados: string[]): Record<string, CampoImport | ''> {
  const usados = new Set<CampoImport>();
  const out: Record<string, CampoImport | ''> = {};
  for (const h of encabezados) {
    const n = sinTildes(h);
    const col = COLUMNAS_IMPORT.find(c => !usados.has(c.campo) && (sinTildes(c.encabezado) === n || c.alias.includes(n)));
    out[h] = col ? col.campo : '';
    if (col) usados.add(col.campo);
  }
  return out;
}

const buscarOpcion = <T extends string>(valor: string, claves: readonly T[], etiqueta: (k: T) => string): T | null => {
  const n = sinTildes(valor);
  return claves.find(k => sinTildes(k) === n || sinTildes(etiqueta(k)) === n) ?? null;
};
export const categoriaDeTexto = (v: string): Categoria | null =>
  buscarOpcion(v, LISTA_CATEGORIAS, k => CATEGORIAS[k].etiqueta)
  ?? LISTA_CATEGORIAS.find(k => sinTildes(CATEGORIAS[k].etiqueta).startsWith(sinTildes(v)) && sinTildes(v).length >= 4) ?? null;

/** Fecha ISO desde una celda: número de serie de Excel, AAAA-MM-DD o DD/MM/AAAA. null = vacía; undefined = inválida. */
export function fechaDeCelda(v: unknown): string | null | undefined {
  if (v === null || v === undefined || String(v).trim() === '') return null;
  if (typeof v === 'number' && v > 59 && v < 80000) {
    const d = new Date(Date.UTC(1899, 11, 30) + Math.round(v) * 86400000);
    return d.toISOString().slice(0, 10);
  }
  const s = String(v).trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (m) return valida(+m[1], +m[2], +m[3]);
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
  if (m) return valida(+m[3], +m[2], +m[1]);
  return undefined;
}
function valida(a: number, m: number, d: number): string | undefined {
  const f = new Date(Date.UTC(a, m - 1, d));
  if (f.getUTCFullYear() !== a || f.getUTCMonth() !== m - 1 || f.getUTCDate() !== d) return undefined;
  return `${a}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export interface ContextoImport {
  sedes: { id: string; name: string }[];
  /** Códigos y números de serie que ya existen en la empresa (también archivados). */
  existentes: Pick<FilaActivo, 'code' | 'serial_number'>[];
}

export type FilaNueva = Partial<Pick<FilaActivo, 'name' | 'code' | 'category' | 'manufacturer' | 'brand' | 'model' | 'serial_number'
  | 'manufacture_year' | 'site_id' | 'area' | 'location_detail' | 'criticality' | 'operational_status' | 'description' | 'external_provider'
  | 'acquired_on' | 'installed_on' | 'useful_life_years'>>;

export interface FilaRevisada { numero: number; datos: FilaNueva; errores: string[]; avisos: string[] }
export interface Revision { filas: FilaRevisada[]; validas: number; conErrores: number; conAvisos: number }

const txt = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim());

/** Valida las filas del archivo con la relación de columnas elegida. numero = fila de la hoja (encabezado en la 1). */
export function revisarFilas(filas: Record<string, unknown>[], mapeo: Record<string, CampoImport | ''>, ctx: ContextoImport): Revision {
  const codigosBase = new Set(ctx.existentes.map(e => normalizarCodigo(e.code)));
  const seriesBase = new Set(ctx.existentes.map(e => sinTildes(e.serial_number ?? '')).filter(Boolean));
  const codigosArchivo = new Map<string, number>();
  const seriesArchivo = new Map<string, number>();
  const revisadas: FilaRevisada[] = [];
  filas.forEach((f, i) => {
    const numero = i + 2;
    const v: Partial<Record<CampoImport, unknown>> = {};
    for (const [h, campo] of Object.entries(mapeo)) if (campo) v[campo] = f[h];
    if (Object.values(v).every(x => txt(x) === '')) return;   // fila vacía: se ignora
    const errores: string[] = [];
    const avisos: string[] = [];
    const d: FilaNueva = {};
    d.name = txt(v.name);
    if (!d.name) errores.push('Falta el nombre.');
    else if (d.name.length > 160) errores.push('El nombre pasa de 160 caracteres.');
    const cat = txt(v.category);
    const categoria = cat ? categoriaDeTexto(cat) : null;
    if (!cat) errores.push('Falta la categoría.');
    else if (!categoria) errores.push(`Categoría no reconocida: "${cat}". Usa una de la hoja Categorías.`);
    else d.category = categoria;
    const codigo = normalizarCodigo(txt(v.code));
    if (codigo) {
      if (codigo.length > 40) errores.push('El código pasa de 40 caracteres.');
      if (codigosBase.has(codigo)) errores.push(`El código ${codigo} ya existe en el inventario.`);
      if (codigosArchivo.has(codigo)) errores.push(`El código ${codigo} se repite en el archivo (fila ${codigosArchivo.get(codigo)}).`);
      else codigosArchivo.set(codigo, numero);
      d.code = codigo;
    }
    const serie = txt(v.serial_number);
    if (serie) {
      const ns = sinTildes(serie);
      if (seriesBase.has(ns)) avisos.push(`El número de serie ${serie} ya está registrado en otro activo.`);
      if (seriesArchivo.has(ns)) avisos.push(`El número de serie ${serie} se repite en el archivo (fila ${seriesArchivo.get(ns)}).`);
      else seriesArchivo.set(ns, numero);
      d.serial_number = serie;
    }
    for (const k of ['manufacturer', 'brand', 'model', 'area', 'location_detail', 'description', 'external_provider'] as const) {
      const x = txt(v[k]);
      if (x) d[k] = x;
    }
    const anio = txt(v.manufacture_year);
    if (anio) {
      const n = Number(anio);
      if (!Number.isInteger(n) || n < 1900 || n > 2100) errores.push(`Año de fabricación no válido: "${anio}".`);
      else d.manufacture_year = n;
    }
    const vida = txt(v.useful_life_years).replace(',', '.');
    if (vida) {
      const n = Number(vida);
      if (!(n > 0 && n <= 100)) errores.push(`Vida útil no válida: "${vida}" (entre 0 y 100 años).`);
      else d.useful_life_years = n;
    }
    const sede = txt(v.site);
    if (sede) {
      const s = ctx.sedes.find(x => sinTildes(x.name) === sinTildes(sede));
      if (!s) avisos.push(`Sede "${sede}" no encontrada: el activo queda sin sede.`);
      else d.site_id = s.id;
    }
    const crit = txt(v.criticality);
    if (crit) {
      const c = buscarOpcion<Criticidad>(crit, CRITICIDADES, k => ETIQUETA_CRITICIDAD[k]);
      if (!c) errores.push(`Criticidad no reconocida: "${crit}" (baja, media, alta o crítica).`);
      else d.criticality = c;
    }
    const est = txt(v.operational_status);
    if (est) {
      const e = buscarOpcion<EstadoOperativo>(est, ESTADOS, k => ETIQUETA_ESTADO[k]);
      if (!e) errores.push(`Estado no reconocido: "${est}".`);
      else d.operational_status = e;
    }
    for (const k of ['acquired_on', 'installed_on'] as const) {
      const f2 = fechaDeCelda(v[k]);
      if (f2 === undefined) errores.push(`Fecha no válida en ${k === 'acquired_on' ? 'adquisición' : 'instalación'}: "${txt(v[k])}" (usa AAAA-MM-DD o DD/MM/AAAA).`);
      else if (f2) d[k] = f2;
    }
    revisadas.push({ numero, datos: d, errores, avisos });
  });
  return {
    filas: revisadas,
    validas: revisadas.filter(r => r.errores.length === 0).length,
    conErrores: revisadas.filter(r => r.errores.length > 0).length,
    conAvisos: revisadas.filter(r => r.errores.length === 0 && r.avisos.length > 0).length,
  };
}

// ── Archivos ──
/** Lee la primera hoja de un Excel o CSV: encabezados (fila 1) y filas como objetos. */
export function leerHoja(datos: ArrayBuffer): { encabezados: string[]; filas: Record<string, unknown>[] } {
  const wb = XLSX.read(datos, { type: 'array', cellDates: false });
  const hoja = wb.Sheets[wb.SheetNames[0]];
  if (!hoja) return { encabezados: [], filas: [] };
  const matriz = XLSX.utils.sheet_to_json<unknown[]>(hoja, { header: 1, defval: '', raw: true });
  const encabezados = ((matriz[0] ?? []) as unknown[]).map(h => String(h ?? '').trim()).filter(Boolean);
  const filas = XLSX.utils.sheet_to_json<Record<string, unknown>>(hoja, { defval: '', raw: true });
  return { encabezados, filas };
}

/** Libro de la plantilla: hoja Activos (encabezados y una fila de ejemplo) y hoja Categorías (valores válidos). */
export function libroPlantilla(): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  const hoja = XLSX.utils.aoa_to_sheet([COLUMNAS_IMPORT.map(c => c.encabezado + (c.requerido ? ' *' : '')), COLUMNAS_IMPORT.map(c => c.ejemplo)]);
  XLSX.utils.book_append_sheet(wb, hoja, 'Activos');
  const listas = XLSX.utils.aoa_to_sheet([
    ['Categoría', 'Criticidad', 'Estado operativo'],
    ...LISTA_CATEGORIAS.map((k, i) => [CATEGORIAS[k].etiqueta, CRITICIDADES[i] ? ETIQUETA_CRITICIDAD[CRITICIDADES[i]] : '', ESTADOS[i] ? ETIQUETA_ESTADO[ESTADOS[i]] : '']),
  ]);
  XLSX.utils.book_append_sheet(wb, listas, 'Categorías');
  const ayuda = XLSX.utils.aoa_to_sheet([
    ['Cómo llenar la plantilla'],
    ['Las columnas con * son obligatorias. Deja el código vacío para que StorSafe lo asigne (por ejemplo MAQ-0001).'],
    ['Fechas: AAAA-MM-DD o DD/MM/AAAA. La aptitud de seguridad no se importa: se revisa y se cambia de a un activo.'],
    ['Antes de guardar verás una vista previa con los errores y los duplicados (por código o número de serie).'],
  ]);
  XLSX.utils.book_append_sheet(wb, ayuda, 'Ayuda');
  return wb;
}

/** "Encabezado *" de la plantilla → el mismo campo que "Encabezado". */
export const limpiarEncabezado = (h: string) => h.replace(/\s*\*\s*$/, '');

export interface ContextoExport { sede: (id: string | null) => string; responsable: (a: FilaActivo) => string; vencimiento: (a: FilaActivo) => string }

/** Filas de la exportación (lo visible en el tablero). */
export function filasExport(activos: FilaActivo[], ctx: ContextoExport): Record<string, string | number>[] {
  return activos.map(a => ({
    'Código': a.code, 'Nombre': a.name, 'Categoría': etiquetaCategoria(a.category), 'Fabricante': a.manufacturer ?? '', 'Marca': a.brand ?? '',
    'Modelo': a.model ?? '', 'Número de serie': a.serial_number ?? '', 'Año de fabricación': a.manufacture_year ?? '', 'Sede': ctx.sede(a.site_id),
    'Área': a.area ?? '', 'Ubicación específica': a.location_detail ?? '', 'Responsable': ctx.responsable(a),
    'Criticidad': ETIQUETA_CRITICIDAD[a.criticality], 'Estado operativo': ETIQUETA_ESTADO[a.operational_status],
    'Aptitud de seguridad': ETIQUETA_APTITUD[a.safety_status], 'Próximo vencimiento': ctx.vencimiento(a), 'Archivado': a.archived_at ? 'Sí' : 'No',
  }));
}

export function descargarLibro(wb: XLSX.WorkBook, nombre: string): void {
  XLSX.writeFile(wb, nombre);
}

export function libroExport(filas: Record<string, string | number>[]): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filas.length ? filas : [{ 'Código': 'Sin activos para exportar' }]), 'Inventario');
  return wb;
}
